import React, { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import styles from './filelist.module.scss'
import OptionsIcon from '../../../shared/components/svgIcons/OptionsIcon'
import FileCardOptionsPopup from './FileCardOptionsPopup'
import RenameTitle from '../RenameTitle'
import CheckIcon from '../../../shared/components/svgIcons/CheckIcon'
import AddedByIcon from '../../../shared/components/svgIcons/AddedByIcon'
import OpenIcon from '../../../shared/components/svgIcons/OpenIcon'
import ReadOnlyIcon from '../../../shared/components/svgIcons/ReadOnlyIcon'
import { useWorkspace } from '../WorkspaceContext'
import { DRAG_MIME } from '../../../shared/utils/driveDnd'
import { makeDragGhost } from '../sidebar/makeDragGhost'
import api from '../../../api'

type FileListTabProps = {
    rType: "file" | "snippet" | "folder";
    rId: string;
    perm?: string;
    creatorId: string;
    creatorName?: string;
    showCreator?: boolean;
    fileSize?: number;
    // contents are what other files and folders it contains
    contents?: string[];
    iconSrc: string;
    resName: string;
    // base name used for renaming: same as resName for folders, but without
    // the extension FileList appends to a snippet's display name
    editName?: string;
    meta?: string;
    onOpen?: (rId: string, rType: "file" | "snippet" | "folder") => void;
    canDrag?: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    canAcceptDrop?: boolean;
    isDragOver?: boolean;
    onDragEnterTarget?: () => void;
    onDragLeaveTarget?: () => void;
    onDropTarget?: () => void;
    // set while a search result lives in a different folder than the one
    // currently shown, so the card can surface where it actually lives
    locationLabel?: string;
    onLocationClick?: () => void;
    selectMode?: boolean;
    isSelected?: boolean;
    onToggleSelect?: (e: React.MouseEvent) => void;
}

const permLabel = (rType: "file" | "snippet" | "folder", perm?: string): string | null => {
    if (rType === 'snippet') return perm === 'edit' ? 'Open' : 'Read Only';
    if (rType === 'folder') return perm === 'open' ? 'Open' : 'Restricted';
    return null;
};

const FileCard = ({
    rType,
    rId,
    perm,
    creatorId,
    creatorName,
    showCreator = false,
    fileSize,
    contents,
    iconSrc,
    resName,
    editName,
    meta,
    onOpen,
    canDrag = false,
    onDragStart,
    onDragEnd,
    canAcceptDrop = false,
    isDragOver = false,
    onDragEnterTarget,
    onDragLeaveTarget,
    onDropTarget,
    locationLabel,
    onLocationClick,
    selectMode = false,
    isSelected = false,
    onToggleSelect,
}: FileListTabProps) => {
    const { roomId } = useParams();
    const { setLimitMessage, setLimitPopup } = useWorkspace();
    const dragOverClass = isDragOver && canAcceptDrop ? styles.fileCardDragOver ?? '' : '';
    const settingsBtnRef = useRef<HTMLDivElement | null>(null);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const dragGhostCleanupRef = useRef<(() => void) | null>(null);

    const openOptions = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOptionsOpen(true);
    };

    const commitRename = async (next: string) => {
        setIsRenaming(false);
        if (!roomId) return;

        try {
            if (rType === 'snippet') {
                await api.patch(`/snippets/updatesnippet/${roomId}/${rId}`, { snippetName: next });
            } else if (rType === 'folder') {
                await api.patch(`/rooms/${roomId}/folders/${rId}/rename`, { fName: next });
            }
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.response?.data?.message || 'Could not rename item. Please try again.';
            setLimitMessage(message);
            setLimitPopup(true);
        }
    };

    const perLabel = permLabel(rType, perm);
    const folderLocationIcon = `${process.env.PUBLIC_URL}/images/folder-closed-dark.png`;
    const PermissionIcon = perm === 'edit' || perm === 'open' ? OpenIcon : ReadOnlyIcon;

    return (
        <div
            className={`${styles.fileCard} ${locationLabel ? styles.fileCardWithLocation : ''} ${dragOverClass} ${canDrag ? styles.fileCardDraggable ?? '' : ''}`}
            onDoubleClick={() => onOpen?.(rId, rType)}
            role="button"
            tabIndex={0}
            title="Double-click to open"
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen?.(rId, rType);
                }
            }}
            data-resource-id={rId}
            data-resource-type={rType}
            data-permission={perm}
            data-creator-id={creatorId}
            draggable={canDrag && !isRenaming}
            onDragStart={(e) => {
                if (!canDrag) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ kind: rType, id: rId }));
                try {
                    dragGhostCleanupRef.current = makeDragGhost(e.nativeEvent as unknown as DragEvent, { label: resName, iconSrc });
                } catch {
                    dragGhostCleanupRef.current = null;
                }
                onDragStart?.();
            }}
            onDragEnd={() => {
                if (dragGhostCleanupRef.current) {
                    dragGhostCleanupRef.current();
                    dragGhostCleanupRef.current = null;
                }
                onDragEnd?.();
            }}
            onDragOver={(e) => {
                if (rType !== 'folder') return;
                e.preventDefault();
                e.dataTransfer.dropEffect = canAcceptDrop ? 'move' : 'none';
            }}
            onDragEnter={(e) => {
                if (rType !== 'folder') return;
                e.preventDefault();
                onDragEnterTarget?.();
            }}
            onDragLeave={(e) => {
                if (rType !== 'folder') return;
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                onDragLeaveTarget?.();
            }}
            onDrop={(e) => {
                if (rType !== 'folder') return;
                e.preventDefault();
                e.stopPropagation();
                onDropTarget?.();
            }}
        >
            <div
                className={`${styles.fcCheckbox} ${isSelected ? styles.fcCheckboxChecked : ''} ${selectMode ? styles.fcCheckboxVisible : ''}`}
                onClick={(e) => { e.stopPropagation(); onToggleSelect?.(e); }}
            >
                {isSelected && <CheckIcon />}
            </div>

            <div className={styles.fcRow}>
                <div className={styles.fcImg}>
                    <img draggable={false} src={iconSrc} alt={resName} />
                </div>

                <div className={styles.fcNameCol}>
                    {isRenaming ? (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                        >
                            <RenameTitle
                                value={editName ?? resName}
                                isEditing={isRenaming}
                                className={styles.fcRenameTitle}
                                onCommit={commitRename}
                                onCancel={() => setIsRenaming(false)}
                            />
                        </div>
                    ) : (
                        <span className={styles.fcName}>{resName}</span>
                    )}

                    <span className={styles.fcMeta}>{meta ?? (rType === 'folder' ? `${contents?.length ?? 0} items` : `${fileSize ?? 0} B`)}</span>
                </div>

                <div ref={settingsBtnRef} className={styles.fcSettingsBtn} onClick={openOptions}>
                    <OptionsIcon />
                </div>
            </div>

            {locationLabel && (
                <div
                    className={styles.fcLocation}
                    onClick={(e) => { e.stopPropagation(); onLocationClick?.(); }}
                    title={`Show in ${locationLabel}`}
                >
                    <img draggable={false} src={folderLocationIcon} alt="" aria-hidden="true" />
                    <span>
                        <strong>In</strong>
                        {locationLabel}
                    </span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>
            )}

            {((showCreator && creatorName) || perLabel) && (
                <div className={styles.fcBottomRow}>
                    {showCreator && creatorName && (
                        <div className={`${styles.fcInfoPill} ${styles.fcCreatorPill}`}>
                            <AddedByIcon />
                            <span className={styles.fcCreatorName}>{creatorName}</span>
                        </div>
                    )}
                    {perLabel && (
                        <div className={`${styles.fcInfoPill} ${styles.fcPermBadge}`}>
                            <PermissionIcon />
                            <span>{perLabel}</span>
                        </div>
                    )}
                </div>
            )}

            {canDrag && (
                <div className={styles.fcDragHint} aria-hidden="true" title="Drag to move">
                    <svg viewBox="0 0 16 16">
                        <circle cx="5" cy="3" r="1.3" />
                        <circle cx="11" cy="3" r="1.3" />
                        <circle cx="5" cy="8" r="1.3" />
                        <circle cx="11" cy="8" r="1.3" />
                        <circle cx="5" cy="13" r="1.3" />
                        <circle cx="11" cy="13" r="1.3" />
                    </svg>
                </div>
            )}

            {optionsOpen && settingsBtnRef.current && (
                <FileCardOptionsPopup
                    rType={rType}
                    rId={rId}
                    creatorId={creatorId}
                    anchorRect={settingsBtnRef.current.getBoundingClientRect()}
                    onClose={() => setOptionsOpen(false)}
                    onOpen={() => {
                        setOptionsOpen(false);
                        onOpen?.(rId, rType);
                    }}
                    onRename={() => setIsRenaming(true)}
                />
            )}
        </div>
    )
}

export default FileCard
